import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  createHttpLink,
  from,
  InMemoryCache,
  Observable,
  type FetchResult,
} from '@apollo/client';
import type { NextLink, Operation } from '@apollo/client/core';
import { onError, type ErrorResponse } from '@apollo/client/link/error';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import type {
  FragmentDefinitionNode,
  OperationDefinitionNode,
  SelectionSetNode,
} from 'graphql';
import { Amplify, Auth } from 'aws-amplify';
import { AuthOptions, createAuthLink } from 'aws-appsync-auth-link';
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link';

import { App } from './app';
import { CONSTANTS } from './shared/constants';
import {
  AuthProvider,
  CookieProvider,
  HumioIntegrationProvider,
  LogScaleProvider,
  OrgTenantsProvider,
  SlideDrawerProvider,
  UISettingsProvider,
} from './shared/providers';
import { DetectionProvider } from './features/detections/context';
import { isGovCloudRegion, getCognitoRegion } from './shared/utils/isGovCloud';

// ── Amplify config ────────────────────────────────────────────────────────────
const poolId =
  window.__env__?.cognito.cognito_user_pool ??
  process.env.REACT_APP_COGNITO_USER_POOL ??
  '';
const region = getCognitoRegion();
const clientId =
  window.__env__?.cognito.userPoolWebClientId ??
  process.env.REACT_APP_COGNITO_WEB_CLIENT_ID ??
  '';

const isGov = isGovCloudRegion(region);
const fips = isGov ? '-fips' : '';
const endpoint = `https://cognito-idp${fips}.${region}.amazonaws.com/`;

Amplify.configure({
  Auth: {
    userPoolId: poolId,
    region,
    userPoolWebClientId: clientId,
    storage: window.localStorage,
    ...(isGov && { endpoint }),
  },
});

Auth.configure({
  userPoolId: poolId,
  region,
  userPoolWebClientId: clientId,
  storage: window.localStorage,
  ...(isGov && { endpoint }),
});

// ── Helpers ───────────────────────────────────────────────────────────────────
const isInSSOAuthFlow = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has('code') && !!window.sessionStorage.getItem('sso_domain');
};

const getTokenSafely = async (): Promise<string | null> => {
  try {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  } catch {
    const storedToken = localStorage.getItem('docs_id_token');
    if (storedToken && storedToken !== 'undefined' && storedToken !== 'null') {
      return storedToken;
    }
    return null;
  }
};

// Build a shape-correct "skipped" result so Apollo can write it quietly.
// Fragment-aware at the root: expands inline and named fragments to collect root fields.
const buildSkippedResultData = (operation: Operation): Record<string, null> => {
  const data: Record<string, null> = {};

  // Pull the operation definition (query/mutation/subscription)
  const operationDef = operation.query.definitions.find(
    (definition): definition is OperationDefinitionNode =>
      definition.kind === 'OperationDefinition',
  );
  if (!operationDef) return data;

  // Index named fragments for quick lookup
  const fragmentsByName = new Map<string, FragmentDefinitionNode>();
  for (const definition of operation.query.definitions) {
    if (definition.kind === 'FragmentDefinition') {
      fragmentsByName.set(definition.name.value, definition);
    }
  }

  // Collect ONLY root-level fields (expand fragments at the root)
  const collectRootFields = (selectionSet: SelectionSetNode): void => {
    for (const selection of selectionSet.selections) {
      switch (selection.kind) {
        case 'Field': {
          const key = selection.alias?.value ?? selection.name.value;
          data[key] = null; // root field -> null
          break; // do not descend into nested fields
        }
        case 'InlineFragment': {
          collectRootFields(selection.selectionSet);
          break;
        }
        case 'FragmentSpread': {
          const fragment = fragmentsByName.get(selection.name.value);
          if (fragment) collectRootFields(fragment.selectionSet);
          break;
        }
      }
    }
  };

  collectRootFields(operationDef.selectionSet);
  return data;
};
// Narrow a network error to get a status code without unsafe casts
const getStatusCode = (networkError: unknown): number | undefined => {
  if (
    networkError &&
    typeof networkError === 'object' &&
    !Array.isArray(networkError)
  ) {
    const ne = networkError as {
      statusCode?: number;
      status?: number;
      result?: { status?: number };
    };
    return ne.statusCode ?? ne.status ?? ne.result?.status;
  }
  return undefined;
};

// ── Links ─────────────────────────────────────────────────────────────────────

// 1) Auth gate link - prevents requests when SSO callback is active or no token
const authGateLink = new ApolloLink(
  (operation: Operation, forward?: NextLink) =>
    new Observable<FetchResult>((observer) => {
      (async () => {
        try {
          const opName = operation.operationName;
          const skipAuthGate = false; // hook for public ops if needed

          // helper: emit a benign "skipped" payload
          type SkippedExt = { skippedByAuthGate: true; op: string };
          const emitSkipped = () => {
            const payload: FetchResult<
              Record<string, null>,
              Record<string, unknown>,
              SkippedExt
            > = {
              data: buildSkippedResultData(operation),
              extensions: { skippedByAuthGate: true, op: opName },
            };
            observer.next(payload);
            observer.complete();
          };

          if (!skipAuthGate) {
            if (isInSSOAuthFlow()) {
              emitSkipped();
              return;
            }
            const token = await getTokenSafely();
            if (!token) {
              emitSkipped();
              return;
            }
          }

          if (!forward) {
            observer.complete();
            return;
          }

          const sub = forward(operation).subscribe({
            next: (d) => observer.next(d),
            error: (e) => observer.error(e),
            complete: () => observer.complete(),
          });

          return () => sub.unsubscribe();
        } catch (error) {
          observer.error(error as Error);
        }
      })();
    }),
);

// 2) Auth recovery on 401 link
const authRecoveryOn401 = onError(
  ({ graphQLErrors, networkError, operation, forward }: ErrorResponse) => {
    const status = getStatusCode(networkError);
    const isUnauthGraphQLError = (graphQLErrors ?? []).some(
      (e) =>
        e?.extensions?.code === 'UNAUTHENTICATED' ||
        e?.extensions?.errorType === 'Unauthorized' ||
        /not authorized/i.test(e?.message ?? ''),
    );

    type RetryCtx = { __alreadyRetried401?: boolean };
    const alreadyRetried = Boolean(
      (operation.getContext() as RetryCtx).__alreadyRetried401,
    );

    if (isInSSOAuthFlow()) return;

    if ((status === 401 || isUnauthGraphQLError) && !alreadyRetried) {
      operation.setContext((prev: RetryCtx) => ({
        ...prev,
        __alreadyRetried401: true,
      }));

      return new Observable<FetchResult>((observer) => {
        (async () => {
          try {
            await Auth.currentAuthenticatedUser({ bypassCache: true });
            await Auth.currentSession();
          } catch (e) {
            observer.error(
              e instanceof Error
                ? e
                : new Error(
                    `Auth recovery failed: ${JSON.stringify(
                      graphQLErrors ?? networkError ?? e,
                    )}`,
                  ),
            );
            return;
          }

          if (!forward) {
            observer.complete();
            return;
          }

          const sub = forward(operation).subscribe({
            next: (v) => observer.next(v),
            error: (e) => observer.error(e),
            complete: () => observer.complete(),
          });

          return () => sub.unsubscribe();
        })();
      });
    }
  },
);

// 3) Remove typename link + HTTP link
const removeTypenameLink = removeTypenameFromVariables();
const httpLink = createHttpLink({ uri: CONSTANTS.APPSYNC.url });
const appsyncHttpChain: ApolloLink = from([removeTypenameLink, httpLink]);

// 4) Compose the link chain with auth gate FIRST
const { url, region: appsyncRegion, auth } = CONSTANTS.APPSYNC;
const appsyncLinkWithAuth = ApolloLink.from([
  authGateLink,
  authRecoveryOn401,
  createAuthLink({ url, region: appsyncRegion, auth: auth as AuthOptions }),
  createSubscriptionHandshakeLink(
    { url, region: appsyncRegion, auth: auth as AuthOptions },
    appsyncHttpChain,
  ),
]);

const cache = new InMemoryCache();

export const client = process.env.NX_MOCK_API
  ? new ApolloClient({ uri: CONSTANTS.MOCK_API.URL, cache })
  : new ApolloClient({ link: appsyncLinkWithAuth, cache });

export const Root = () => {
  return (
    <ApolloProvider client={client}>
      <AuthProvider>
        <CookieProvider>
          <OrgTenantsProvider>
            <HumioIntegrationProvider>
              <DetectionProvider>
                <LogScaleProvider>
                  <SlideDrawerProvider>
                    <UISettingsProvider>
                      <App />
                    </UISettingsProvider>
                  </SlideDrawerProvider>
                </LogScaleProvider>
              </DetectionProvider>
            </HumioIntegrationProvider>
          </OrgTenantsProvider>
        </CookieProvider>
      </AuthProvider>
    </ApolloProvider>
  );
};
